import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Fetch-on-mount with cancellation.
 *
 * The catalogue re-fetches on every filter keystroke, so a slow early request must never
 * overwrite a fast later one: each run takes a sequence number and only the newest is
 * allowed to commit. `deps` is a serialised key rather than an array so callers can pass
 * a whole query object without re-render churn.
 */
export function useFetch(fn, key, { skip = false } = {}) {
  const [state, setState] = useState({ data: null, loading: !skip, error: null });
  const seq = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(async () => {
    if (skip) { setState({ data: null, loading: false, error: null }); return; }
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fnRef.current();
      if (mine === seq.current) setState({ data, loading: false, error: null });
    } catch (error) {
      if (mine === seq.current) setState({ data: null, loading: false, error });
    }
  }, [skip]);

  useEffect(() => { run(); }, [key, run]);

  return { ...state, reload: run };
}

/** Debounce a value — used for the search box so typing does not fire a query per keypress. */
export function useDebounced(value, ms = 320) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
